import { useEffect, useMemo, useState } from 'react';

import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import FilterAltRoundedIcon from '@mui/icons-material/FilterAltRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import {
  Autocomplete,
  Badge,
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import moment from 'moment';
import { useQuery } from 'react-query';
import { useHistory } from 'react-router-dom';

import { useAuthContext } from '../../../context/AuthContextProvider';
import {
  getArticlesFilters,
  getOperationsRelatedArticles,
} from '../../../services/article';
import {
  getCategories,
  getInstitutionsRelatedArticles,
  getMarkets,
} from '../../../services/institution';
import { getProfessionalsRelatedArticles } from '../../../services/professional';

const EMPTY_FILTERS = {
  search: '',
  categories: [],
  markets: [],
  relatedInstitutions: [],
  relatedProfessionals: [],
  relatedOperations: [],
};

const FILTER_FIELDS = [
  { name: 'categories', label: 'Categorias' },
  { name: 'markets', label: 'Mercados' },
  { name: 'relatedInstitutions', label: 'Instituições relacionadas' },
  { name: 'relatedProfessionals', label: 'Profissionais relacionados' },
  { name: 'relatedOperations', label: 'Operações relacionadas' },
];

const uniqueOptions = (values) =>
  [...new Set(values.filter(Boolean))].sort((first, second) =>
    first.localeCompare(second),
  );

const toInputDate = (value) =>
  value ? moment(value).format('YYYY-MM-DD') : '';
const toFilterDate = (value) => (value ? moment(value).format() : null);

const getSelectedFiltersFromProps = (filters) => ({
  search: filters.keyword ?? '',
  categories: filters.categories ?? [],
  markets: filters.markets ?? [],
  relatedInstitutions: filters.relatedInstitutions ?? [],
  relatedProfessionals: filters.relatedProfessionals ?? [],
  relatedOperations: filters.relatedOperations ?? [],
});

const getDateRangeFromProps = (filters) => ({
  start: filters.dateRange?.start ?? null,
  end: filters.dateRange?.end ?? null,
});

function ArticlesAutocomplete({
  disabled,
  label,
  loading,
  name,
  onChange,
  options,
  value,
}) {
  return (
    <Autocomplete
      multiple
      disableCloseOnSelect
      disabled={disabled || loading}
      loading={loading}
      options={options}
      value={value}
      onChange={(_, nextValue) => onChange(name, nextValue)}
      renderInput={(params) => (
        <TextField {...params} label={label} placeholder="Selecionar" />
      )}
      size="small"
    />
  );
}

export default function HeaderArticles({
  filters,
  resultCount,
  sort,
  setFilters,
  setSort,
}) {
  const { auth } = useAuthContext();
  const history = useHistory();
  const institutionName = new URL(window.location).searchParams.get(
    'instituicao',
  );
  const institutionId = new URL(window.location).searchParams.get('id');
  const initialRelatedInstitutions =
    auth.isLoggedIn && institutionName && institutionId
      ? [institutionName]
      : [];
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedFilterChip, setSelectedFilterChip] = useState({});
  const [dateRange, setDateRange] = useState(() =>
    getDateRangeFromProps(filters),
  );
  const [draftDateRange, setDraftDateRange] = useState({
    start: null,
    end: null,
  });
  const [selectedFilters, setSelectedFilters] = useState(() =>
    getSelectedFiltersFromProps(filters),
  );
  const [draftFilters, setDraftFilters] = useState(() =>
    getSelectedFiltersFromProps(filters),
  );
  const [searchValue, setSearchValue] = useState(filters.keyword ?? '');
  const hasPermission =
    auth?.user?.subscription?.plan?.permissions.artigos_filtros;

  const { data: chipFilters } = useQuery('chipFilters', () =>
    getArticlesFilters(),
  );

  const { data: marketsData, isLoading: marketsLoading } = useQuery(
    'markets',
    getMarkets,
  );
  const { data: categoriesData, isLoading: categoriesLoading } = useQuery(
    'categories',
    getCategories,
  );
  const { data: institutionsData, isLoading: institutionsLoading } = useQuery(
    'institutions',
    getInstitutionsRelatedArticles,
  );
  const { data: professionalsData, isLoading: professionalsLoading } = useQuery(
    'professionals',
    getProfessionalsRelatedArticles,
  );
  const { data: operationsData, isLoading: operationsLoading } = useQuery(
    'operations',
    getOperationsRelatedArticles,
  );

  const options = useMemo(
    () => ({
      categories: uniqueOptions(
        categoriesData?.data?.map((category) => category.title) ?? [],
      ),
      markets: uniqueOptions(
        marketsData?.data?.map((market) => market.name) ?? [],
      ),
      relatedInstitutions: uniqueOptions(
        institutionsData?.data?.map((institution) => institution.razaoSocial) ??
          [],
      ),
      relatedProfessionals: uniqueOptions(
        professionalsData?.data?.map(
          ({ name, surname }) => `${name} ${surname}`,
        ) ?? [],
      ),
      relatedOperations: uniqueOptions(
        operationsData?.data?.flatMap((article) =>
          article.relatedOperations.map((operation) => operation.name.trim()),
        ) ?? [],
      ),
    }),
    [
      categoriesData,
      institutionsData,
      marketsData,
      operationsData,
      professionalsData,
    ],
  );

  const activeFilterChips = useMemo(() => {
    const entries = [
      ['keyword', 'Busca', selectedFilters.search],
      ['categories', 'Categoria', selectedFilters.categories],
      ['markets', 'Mercado', selectedFilters.markets],
      [
        'relatedInstitutions',
        'Instituição',
        selectedFilters.relatedInstitutions,
      ],
      [
        'relatedProfessionals',
        'Profissional',
        selectedFilters.relatedProfessionals,
      ],
      ['relatedOperations', 'Operação', selectedFilters.relatedOperations],
    ];

    const chips = entries.flatMap(([field, label, value]) =>
      Array.isArray(value)
        ? value.map((item) => ({ field, label, value: item }))
        : value
          ? [{ field, label, value }]
          : [],
    );

    if (dateRange.start) {
      chips.push({
        field: 'startDate',
        label: 'De',
        value: moment(dateRange.start).format('DD/MM/YYYY'),
      });
    }

    if (dateRange.end) {
      chips.push({
        field: 'endDate',
        label: 'Até',
        value: moment(dateRange.end).format('DD/MM/YYYY'),
      });
    }

    return chips;
  }, [dateRange, selectedFilters]);

  const drawerFiltersCount = useMemo(
    () =>
      FILTER_FIELDS.reduce(
        (total, { name }) => total + selectedFilters[name].length,
        0,
      ) +
      Number(Boolean(dateRange.start)) +
      Number(Boolean(dateRange.end)),
    [dateRange, selectedFilters],
  );

  const loadingByField = {
    categories: categoriesLoading,
    markets: marketsLoading,
    relatedInstitutions: institutionsLoading,
    relatedProfessionals: professionalsLoading,
    relatedOperations: operationsLoading,
  };

  const commitFilters = (nextFilters, nextDateRange = dateRange) => {
    const hasSearch = Boolean(nextFilters.search?.trim());

    if (!hasSearch && sort === 'relevance') {
      setSort('desc');
    }

    setSelectedFilters(nextFilters);
    setDraftFilters(nextFilters);
    setSearchValue(nextFilters.search);
    setDateRange(nextDateRange);
    setDraftDateRange(nextDateRange);
    setFilters({
      keyword: nextFilters.search
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, ''),
      categories: nextFilters.categories,
      markets: nextFilters.markets,
      relatedInstitutions: nextFilters.relatedInstitutions,
      relatedProfessionals: nextFilters.relatedProfessionals,
      relatedOperations: nextFilters.relatedOperations,
      dateRange: nextDateRange,
    });
  };

  const updateSearch = (value) => {
    setSelectedFilterChip({});
    setSearchValue(value);
  };

  const updateDraftFilter = (name, value) => {
    setDraftFilters((current) => ({ ...current, [name]: value }));
  };

  const updateDraftDateRange = (name, value) => {
    setDraftDateRange((current) => ({
      ...current,
      [name]: toFilterDate(value),
    }));
  };

  const applyDrawerFilters = () => {
    setSelectedFilterChip({});
    commitFilters(draftFilters, draftDateRange);
    setDrawerOpen(false);
  };

  const clearFilters = () => {
    const initialFilters = {
      ...EMPTY_FILTERS,
      relatedInstitutions: initialRelatedInstitutions,
    };
    const nextDateRange = { start: null, end: null };

    setSelectedFilterChip({});
    commitFilters(initialFilters, nextDateRange);
  };

  const removeActiveFilter = (field, value) => {
    if (!hasPermission) return;

    setSelectedFilterChip({});

    if (field === 'startDate' || field === 'endDate') {
      const dateField = field === 'startDate' ? 'start' : 'end';
      const nextDateRange = { ...dateRange, [dateField]: null };
      commitFilters(selectedFilters, nextDateRange);
      return;
    }

    if (field === 'keyword') {
      commitFilters({ ...selectedFilters, search: '' });
      return;
    }

    commitFilters({
      ...selectedFilters,
      [field]: selectedFilters[field].filter((item) => item !== value),
    });
  };

  const handleSelectChip = (filter) => {
    if (filter?.id === selectedFilterChip?.id) {
      clearFilters();
      return;
    }

    const nextFilters = {
      search: filter?.keyword ?? '',
      categories: filter?.categories?.map((category) => category.title) ?? [],
      markets: filter?.markets?.map((market) => market.name) ?? [],
      relatedInstitutions:
        filter?.relatedInstitutions?.map(
          (institution) => institution.razaoSocial,
        ) ?? [],
      relatedProfessionals:
        filter?.relatedProfessionals?.map(
          (professional) => professional.fullName,
        ) ?? [],
      relatedOperations:
        filter?.relatedOperations?.map((operation) => operation.name.trim()) ??
        [],
    };
    const nextDateRange = {
      start: filter.startDate ?? null,
      end: filter.endDate ?? null,
    };

    setSelectedFilterChip(filter);
    commitFilters(nextFilters, nextDateRange);
  };

  const openDrawer = () => {
    setDraftFilters(selectedFilters);
    setDraftDateRange(dateRange);
    setDrawerOpen(true);
  };

  useEffect(() => {
    if (searchValue === selectedFilters.search) return undefined;

    const timeout = setTimeout(() => {
      commitFilters({ ...selectedFilters, search: searchValue });
    }, 500);

    return () => clearTimeout(timeout);
  }, [searchValue, selectedFilters]);

  useEffect(() => {
    const state = history.location.state;
    if (state) {
      const nextFilters = {
        ...selectedFilters,
        markets: state.market ?? [],
        categories: state.category ?? [],
      };

      commitFilters(nextFilters);
    }
  }, []);

  return (
    <Box sx={{ py: 3 }}>
      <Paper
        elevation={0}
        sx={{
          border: '1px solid',
          borderColor: 'grey.200',
          borderRadius: 4,
          p: { xs: 2, md: 2.5 },
        }}>
        <Stack spacing={2}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.5}
            alignItems={{ md: 'center' }}>
            <TextField
              disabled={!hasPermission}
              fullWidth
              onChange={(event) => updateSearch(event.target.value)}
              placeholder="Busque por tema, mercado, operação, instituição ou profissional"
              value={searchValue}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon />
                  </InputAdornment>
                ),
              }}
              size="small"
            />
            <FormControl
              disabled={!hasPermission}
              size="small"
              sx={{ minWidth: { xs: '100%', md: 190 } }}>
              <InputLabel id="articles-order-select">Ordenar por</InputLabel>
              <Select
                label="Ordenar por"
                labelId="articles-order-select"
                onChange={(event) => setSort(event.target.value)}
                value={sort}>
                {(selectedFilters.search || searchValue) && (
                  <MenuItem value="relevance">Mais relevantes</MenuItem>
                )}
                <MenuItem value="desc">Mais recentes</MenuItem>
                <MenuItem value="asc">Mais antigos</MenuItem>
              </Select>
            </FormControl>
            <Badge badgeContent={drawerFiltersCount} color="primary">
              <Button
                disabled={!hasPermission}
                onClick={openDrawer}
                startIcon={<TuneRoundedIcon />}
                variant="contained"
                sx={{
                  minHeight: 40,
                  minWidth: { xs: '100%', md: 132 },
                  fontWeight: 800,
                }}>
                Filtros
              </Button>
            </Badge>
          </Stack>

          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={1.5}
            flexWrap="wrap"
            useFlexGap>
            <Typography color="text.secondary" variant="body2">
              {resultCount} resultado{resultCount === 1 ? '' : 's'} encontrado
              {resultCount === 1 ? '' : 's'}
            </Typography>
            <Button
              color="primary"
              onClick={() => history.push('/publicar/artigos')}
              size="small"
              sx={{ fontWeight: 800 }}>
              Publique na Uqbar
            </Button>
          </Stack>

          {activeFilterChips.length > 0 && (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {activeFilterChips.map(({ field, label, value }) => (
                <Chip
                  key={`${field}-${value}`}
                  label={`${label}: ${value}`}
                  onDelete={
                    hasPermission
                      ? () => removeActiveFilter(field, value)
                      : undefined
                  }
                  size="small"
                  variant="outlined"
                />
              ))}
            </Stack>
          )}
        </Stack>
      </Paper>

      {chipFilters?.length > 0 && (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap mt={2}>
          {chipFilters.map((filter) => (
            <Chip
              key={`${filter.id}-chip`}
              clickable={hasPermission}
              color={
                filter?.id === selectedFilterChip?.id ? 'primary' : 'default'
              }
              disabled={!hasPermission}
              icon={<FilterAltRoundedIcon />}
              label={filter.name}
              onClick={() => handleSelectChip(filter)}
              sx={{ fontWeight: 700 }}
              variant={
                filter?.id === selectedFilterChip?.id ? 'filled' : 'outlined'
              }
            />
          ))}
        </Stack>
      )}

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            top: { xs: 150, md: 75 },
            width: { xs: '100vw', sm: 440 },
            maxWidth: '100vw',
            height: { xs: 'calc(100dvh - 150px)', md: 'calc(100dvh - 75px)' },
            overflow: 'hidden',
          },
        }}>
        <Stack height="100%">
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
            sx={{ flexShrink: 0, p: 3, pb: 2 }}>
            <Box>
              <Typography variant="h6" fontWeight={800}>
                Filtros
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Refine os artigos por assunto, relações e data.
              </Typography>
            </Box>
            <IconButton
              aria-label="Fechar filtros"
              onClick={() => setDrawerOpen(false)}>
              <CloseRoundedIcon />
            </IconButton>
          </Stack>

          <Divider />

          <Stack spacing={2.25} sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
            {FILTER_FIELDS.map(({ label, name }) => (
              <ArticlesAutocomplete
                key={name}
                disabled={!hasPermission}
                label={label}
                loading={loadingByField[name]}
                name={name}
                onChange={updateDraftFilter}
                options={options[name]}
                value={draftFilters[name]}
              />
            ))}

            <TextField
              disabled={!hasPermission}
              fullWidth
              InputLabelProps={{ shrink: true }}
              label="Data publicação início"
              onChange={(event) =>
                updateDraftDateRange('start', event.target.value)
              }
              size="small"
              type="date"
              value={toInputDate(draftDateRange.start)}
            />
            <TextField
              disabled={!hasPermission}
              fullWidth
              InputLabelProps={{ shrink: true }}
              label="Data publicação fim"
              onChange={(event) =>
                updateDraftDateRange('end', event.target.value)
              }
              size="small"
              type="date"
              value={toInputDate(draftDateRange.end)}
            />
          </Stack>

          <Divider />

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            sx={{
              flexShrink: 0,
              p: 3,
              pt: 2,
              backgroundColor: 'common.white',
            }}>
            <Button
              disabled={!hasPermission || activeFilterChips.length === 0}
              fullWidth
              onClick={clearFilters}
              startIcon={<CloseRoundedIcon />}
              variant="outlined">
              Limpar filtros
            </Button>
            <Button
              disabled={!hasPermission}
              fullWidth
              onClick={applyDrawerFilters}
              variant="contained">
              Aplicar filtros
            </Button>
          </Stack>
        </Stack>
      </Drawer>
    </Box>
  );
}
